import React, { useMemo } from "react";

import {
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "~/sdks/LocalContent/types";

import { useTracking } from "@local-logic/core/tracking";
import { useTranslation } from "react-i18next";

import { PoiIcon } from "~/components/PoiIcon";

import type { ScoreKey, POISubcategory } from "~/sdks/LocalContent/types";
import type { Markers } from "../types";

import * as styles from "./styles";

export function Subcategories({
  pois,
  scoreKey,
  selectedSubcategory,
  onSelectCategory,
}: {
  pois: Markers;
  scoreKey: ScoreKey | null;
  selectedSubcategory: POISubcategory | null;
  onSelectCategory: (subcategory: POISubcategory | null) => void;
}) {
  const { t } = useTranslation(["localContent", "common"]);
  const tracking = useTracking();

  const subcategories: POISubcategory[] = useMemo(() => {
    if (typeof pois === "undefined" || Object.keys(pois).length === 0) {
      return [];
    }

    const filteredSubcategories = Object.keys(pois).filter(
      (subcategory) =>
        Array.isArray(pois[subcategory as POISubcategory]) &&
        pois[subcategory as POISubcategory]!.length > 0
    );

    return filteredSubcategories as POISubcategory[];
  }, [pois]);

  const handleSubcategoryClick = (subcategory: POISubcategory) => () => {
    if (selectedSubcategory === subcategory) {
      return;
    }

    onSelectCategory(subcategory);

    const eventAttributes = {
      score: scoreKey as string,
      category: subcategory,
    };

    tracking.postEvent({
      eventType: TrackingPipelineEvents.SCORE_POI_CATEGORY_SELECTED,
      eventAttributes: Object.entries(eventAttributes).map(([key, value]) => ({
        label: key,
        data: value,
      })),
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SCORE_POI_CATEGORY_SELECTED,
      eventAttributes,
    });
  };

  return (
    <div
      className={styles.container}
      data-testid="score-poi-list-subcategories"
    >
      {subcategories.length > 1 && (
        <p
          className={styles.title}
          data-testid="score-poi-list-subcategories-title"
        >
          {t("poiList.subcategoriesTitle", { context: scoreKey })}
        </p>
      )}
      {subcategories.length > 0 && (
        <div
          className={styles.subcategories}
          data-testid="score-poi-list-subcategories-list"
        >
          {subcategories.map((subcategory) => (
            <button
              data-testid={`score-poi-list-subcategory-${subcategory}`}
              className={styles.button}
              data-is-active={selectedSubcategory === subcategory}
              key={`score-poi-list-subcategory-${subcategory}`}
              onClick={handleSubcategoryClick(subcategory)}
            >
              <PoiIcon poi={subcategory} />
              {t(`common:pois.${subcategory}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
